<template>
  <div class="channel-create-page">
    <!-- 头部 -->
    <div class="channel-create-header">
      <van-icon name="arrow-left" size="20" color="#333" style="cursor: pointer;" @click="$router.back()" />
      <span class="channel-create-title">{{ $t('channel_create_title') }}</span>
      <span style="width: 20px;"></span>
    </div>

    <!-- 中间可滚动区域：进度卡片 + 聊天消息 -->
    <div class="chat-scroll-area" ref="chatMessages">

    <!-- 顶部进度卡片 -->
    <div v-if="showProgressCard" class="progress-card">
      <div class="progress-header">
        <div class="progress-header-left">
          <van-icon name="star-o" size="16" color="#E8543A" style="position: relative; top: -1px;" />
          <span>{{ isClarified ? $t('channelCreate_readyToCreate') : $t('channelCreate_clarifyingProgress') }}</span>
        </div>
        <span class="progress-reset" @click="resetSession">
          <van-icon name="replay" size="12" color="#E8543A" />
          {{ $t('aiChat_newChat') }}
        </span>
      </div>
      <!-- 步骤进度 -->
      <div class="progress-steps">
        <div class="step-pill" :class="isClarified ? 'step-done' : 'step-active'">
          <van-icon v-if="isClarified" name="success" size="10" color="#34A853" />
          <span v-else class="step-dot-active"></span>
          <span>{{ $t('channelCreate_step1') }}</span>
        </div>
        <div class="step-line" :class="isClarified ? 'line-done' : 'line-pending'"></div>
        <div class="step-pill" :class="isClarified ? 'step-active' : 'step-pending'">
          <span v-if="isClarified" class="step-dot-active"></span>
          <span v-else class="step-dot-pending"></span>
          <span>{{ $t('channelCreate_step2') }}</span>
        </div>
      </div>
      <!-- 完备度/剩余轮次 -->
      <div v-if="!isClarified && clarifyingSubtitle" class="progress-subtitle">
        <span v-if="completeness != null" class="progress-percent">{{ completeness }}%</span>
        <span>{{ clarifyingSubtitle }}</span>
      </div>
      <!-- 确认建群预览卡片 -->
      <div v-if="isClarified" class="progress-preview">
        <div v-if="extractedName" class="preview-row">
          <span class="preview-label">{{ $t('channel_create_name') }}:</span>
          <span class="preview-value">{{ extractedName }}</span>
        </div>
        <div v-if="extractedDescription" class="preview-row">
          <span class="preview-label">{{ $t('channel_create_description') }}:</span>
          <span class="preview-value">{{ extractedDescription }}</span>
        </div>
      </div>
      <!-- 公开提示 -->
      <div v-if="isClarified" class="progress-notice">
        <van-icon name="info-o" size="14" color="#3DAECF" />
        <span>{{ $t('channelCreate_publicNotice') }}</span>
      </div>
      <!-- 确认创建按钮 -->
      <van-button v-if="isClarified" type="primary" block color="#E8543A" :loading="creating"
        :disabled="creating || !extractedName" :loading-text="$t('channelCreate_creating')" @click="showConfirmDialog">
        {{ $t('channelCreate_confirmBtn') }}
      </van-button>
    </div>

    <!-- 聊天消息列表 -->
    <div class="chat-messages">
      <div v-for="(msg, index) in messages" :key="index" class="chat-message" :class="msg.direction === '0' ? 'msg-sent' : 'msg-received'">
        <!-- 头像 -->
        <div class="msg-avatar">
          <img v-if="msg.direction === '0' && currentUserAvatar" :src="currentUserAvatar" class="msg-avatar-img" />
          <div v-else-if="msg.direction === '0'" class="msg-avatar-placeholder">{{ (currentUserName || '我')[0] }}</div>
          <img v-else src="/static/logo-transparent.png" class="msg-avatar-img" />
        </div>
        <!-- 内容 -->
        <div class="msg-content-wrapper">
          <div class="msg-bubble" :class="msg.direction === '0' ? 'bubble-sent' : 'bubble-received'">
            <!-- assistant 类型消息（对齐 ChatMsgList.vue） -->
            <template v-if="msg.contentType === 'assistant'">
              <div style="font-size:0.85rem;padding:2px 0px;color: #1A1A1A;">
                <!-- Think 内容展示（流式中） -->
                <div v-if="msg.isThinking" style="margin-bottom: 8px;">
                  <div style="display: flex;align-items: center;font-size: 0.7rem;color: #8A8A8A;">
                    <van-icon name="clock-o" size="12" style="margin-right: 4px;flex-shrink:0;" />
                    <span style="flex-shrink:0;">{{ $t('thinking') }}...</span>
                    <span v-if="msg.thinkContent" class="thinking-latest-text"><span>{{ getLatestThinkLine(msg.thinkContent) }}</span></span>
                  </div>
                </div>
                <!-- 解析后的消息文本 -->
                <div v-if="msg.parsedMsg && msg.parsedMsg.trim()" v-html="msg.parsedMsg"
                  style="white-space: pre-line;word-break: break-all;" class="html-content"></div>
                <!-- actions 按钮 -->
                <div v-if="msg.actions && msg.actions.length > 0" style="margin-top: 10px;">
                  <div v-for="(action, aIndex) in msg.actions" :key="aIndex">
                    <van-button v-if="action.type === 'pick_location'" size="small" type="primary"
                      @click="handlePickLocation(action)" style="margin-top: 5px;">
                      <van-icon name="location-o" style="margin-right: 4px;" />
                      {{ action.description || $t('select_location') }}
                    </van-button>
                    <van-button v-if="action.type === 'pick_date'" size="small" type="primary"
                      @click="handlePickDate(action)" style="margin-top: 5px;">
                      <van-icon name="calendar-o" style="margin-right: 4px;" />
                      {{ action.description || $t('select_date') }}
                    </van-button>
                  </div>
                </div>
              </div>
            </template>
            <!-- 普通文本消息 -->
            <template v-else>
              <div class="msg-text" v-html="formatMsg(msg)"></div>
            </template>
          </div>
          <div class="msg-meta">
            <span v-if="msg.createdAt">{{ $common.formatMMDDHHMM(msg.createdAt) }}</span>
          </div>
        </div>
      </div>
    </div>
    </div><!-- end chat-scroll-area -->

    <!-- 底部输入框 -->
    <div class="chat-input-area">
      <div class="chat-input-wrapper">
        <input
          v-model="inputText"
          type="text"
          :placeholder="$t('channelCreate_aiHint')"
          class="chat-input"
          :disabled="isSending"
          @keyup.enter="sendMessage"
        />
        <van-button size="small" type="primary" color="#E8543A" :loading="isSending"
          :disabled="!inputText.trim() || isSending" @click="sendMessage">
          {{ $t('send') }}
        </van-button>
      </div>
    </div>

    <!-- 相似群弹窗 -->
    <van-popup v-model="showSimilarHubs" round :style="{ padding: '0', width: '90%', maxWidth: '400px' }">
      <div class="similar-hubs-popup">
        <!-- 标题行 -->
        <div class="similar-hubs-header">
          <div class="similar-hubs-header-left">
            <van-icon name="friends-o" size="22" color="#E8543A" />
            <span>{{ $t('channelCreate_similarGroupsTitle') }}</span>
          </div>
          <van-icon name="cross" size="20" color="#999" style="cursor: pointer;" @click="showSimilarHubs = false" />
        </div>

        <!-- 提示信息 -->
        <div v-if="hasExactMatch" class="similar-hubs-hint exact-match">
          <van-icon name="checked" size="20" color="#34A853" />
          <span>{{ $t('channelCreate_exactMatchHint') || 'A group with this exact name already exists!' }}</span>
        </div>
        <div v-else class="similar-hubs-hint">
          <van-icon name="info-o" size="15" color="#3DAECF" />
          <span>{{ $t('channelCreate_joinHint') || 'These existing groups might meet your needs' }}</span>
        </div>

        <!-- 相似群列表 -->
        <div v-if="similarHubs.length > 0" class="similar-hubs-list">
          <div v-for="hub in similarHubs" :key="hub.channelId" class="similar-hub-item">
            <div class="similar-hub-avatar">
              <img v-if="hub.avatar" :src="$common.getFullImageUrl(hub.avatar, $config)" class="similar-hub-avatar-img" />
              <div v-else class="similar-hub-avatar-placeholder">{{ (hub.name || '?')[0] }}</div>
            </div>
            <div class="similar-hub-info">
              <div class="similar-hub-name">{{ hub.name }}</div>
              <div v-if="hub.description" class="similar-hub-desc">{{ hub.description }}</div>
              <div v-if="hub.memberCnt" class="similar-hub-meta">{{ hub.memberCnt }} {{ $t('channel_detail_members') }}</div>
            </div>
            <van-button
              size="small"
              type="primary"
              color="#34A853"
              :loading="joiningChannelId === hub.channelId"
              :disabled="joiningChannelId === hub.channelId"
              style="flex-shrink: 0; min-width: 70px;"
              @click="joinSimilarHub(hub)"
            >
              {{ $t('channelCreate_joinExisting') }}
            </van-button>
          </div>
        </div>
        <div v-else class="similar-hubs-empty">
          {{ $t('channelCreate_similarGroupsDesc') }}
        </div>

        <!-- 底部仍要创建按钮 -->
        <div class="similar-hubs-footer">
          <van-button plain type="primary" color="#999" block @click="createNewChannel">
            {{ $t('channelCreate_stillCreate') }}: #{{ extractedName }}
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Toast, Button, Popup } from 'vant'
import wsService from '@/services/websocket.js'

Vue.use(Icon)
Vue.use(Toast)
Vue.use(Button)
Vue.use(Popup)

const PLATFORM_USER_ID = '20210802'

export default {
  name: 'ChannelCreate',
  data() {
    return {
      // 聊天相关
      messages: [],
      inputText: '',
      isSending: false,
      chatSessionId: '',
      // AI 提取结果
      requirementClarified: '0',
      extractedName: '',
      extractedDescription: '',
      completeness: null,
      estimatedRemainingRounds: null,
      // 创建中
      creating: false,
      showSimilarHubs: false,
      similarHubs: [],
      hasExactMatch: false,
      joiningChannelId: '',
      // WebSocket
      wsMessageHandler: null,
      wsServerAckHandler: null,
      wsSendFailedHandler: null,
      // 流消息累积
      streamMessages: {},
      // ACK 超时
      ackTimer: null,
      // 流超时
      streamTimeoutTimer: null,
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.user || {}
    },
    currentUserName() {
      return this.currentUser.userName || this.currentUser.email || ''
    },
    currentUserAvatar() {
      return this.currentUser.avatar || this.currentUser.avatarUrl || ''
    },
    isClarified() {
      return this.requirementClarified === '1'
    },
    showProgressCard() {
      return this.messages.length > 0 || this.isClarified
    },
    clarifyingSubtitle() {
      if (this.isClarified) return ''
      const remaining = this.estimatedRemainingRounds
      if (remaining === 0) {
        return this.$t('aiChat_clarifyingAlmostDone')
      } else if (remaining === 1) {
        return this.$t('aiChat_clarifyingOneMore')
      } else if (remaining != null && remaining > 1) {
        return this.$t('aiChat_clarifyingRemaining', { count: remaining })
      }
      return ''
    }
  },
  mounted() {
    this.setupWebSocketListeners()
    // 添加欢迎消息
    this.addBotMessage(this.$t('channelCreate_welcomeMsg'))
  },
  beforeDestroy() {
    this.removeWebSocketListeners()
    this.clearTimers()
  },
  methods: {
    // ===== WebSocket =====
    setupWebSocketListeners() {
      this.wsMessageHandler = (msg) => {
        this.handleIncomingMessage(msg)
      }
      wsService.on('msg', this.wsMessageHandler, 'channel_create_msg')
      this.wsServerAckHandler = (msg) => {
        if (msg && msg.msgId) {
          this.clearTimers()
        }
      }
      this.wsSendFailedHandler = (msg) => {
        if (msg && msg.msgId) {
          this.clearTimers()
          this.isSending = false
          this.addBotMessage('Send failed, please try again.')
        }
      }
      wsService.on('server_ack', this.wsServerAckHandler, 'channel_create_ack')
      wsService.on('send_failed', this.wsSendFailedHandler, 'channel_create_failed')
    },
    removeWebSocketListeners() {
      if (this.wsMessageHandler) wsService.off('msg', 'channel_create_msg')
      if (this.wsServerAckHandler) wsService.off('server_ack', 'channel_create_ack')
      if (this.wsSendFailedHandler) wsService.off('send_failed', 'channel_create_failed')
    },
    clearTimers() {
      if (this.ackTimer) {
        clearTimeout(this.ackTimer)
        this.ackTimer = null
      }
      if (this.streamTimeoutTimer) {
        clearTimeout(this.streamTimeoutTimer)
        this.streamTimeoutTimer = null
      }
    },

    // ===== 消息处理 =====
    handleIncomingMessage(msg) {
      if (!msg) return
      if (msg.contentType === '_server_ack' && msg.msgId) {
        this.clearTimers()
        return
      }
      if (msg.contentType === '_send_failed' && msg.msgId) {
        this.clearTimers()
        this.isSending = false
        this.addBotMessage('Send failed, please try again.')
        return
      }
      // 只处理当前 session 的消息
      if (msg.chatSessionId && msg.chatSessionId !== this.chatSessionId) return
      // 只处理平台用户的消息
      if (msg.fromUserId !== PLATFORM_USER_ID) return
      // 处理流式消息
      if (msg.isStream) {
        this.handleStreamMessage(msg)
        return
      }
      if (msg.contentType === 'assistant') {
        this.clearTimers()
        this.isSending = false
        // 非流式 assistant 消息也需要走 parseThinkTags 解析（与 ChatMsgList.vue 一致）
        let rawContent = msg.msg || ''
        if (typeof rawContent === 'string') {
          try { rawContent = JSON.parse(rawContent) } catch(e) {}
        }
        const actualMsg = (typeof rawContent === 'object' && rawContent !== null) ? (rawContent.msg || '') : rawContent
        const parseResult = this.parseThinkTags(actualMsg)
        this.messages.push({
          msgId: msg.msgId || '',
          chatSessionId: msg.chatSessionId || '',
          fromUserId: msg.fromUserId || '',
          toUserId: msg.toUserId || '',
          contentType: 'assistant',
          msg: actualMsg,
          parsedMsg: this.parseRawContent(parseResult.text),
          thinkContent: parseResult.thinkContent || '',
          isThinking: parseResult.isThinking || false,
          direction: msg.direction || '1',
          createdAt: msg.createdAt || Date.now(),
          actions: parseResult.actions || [],
          completeness: parseResult.completeness,
          estimatedRemainingRounds: parseResult.estimatedRemainingRounds
        })
        this.checkRequirementClarified(parseResult.actions || [], actualMsg)
        this.syncProgressFromMessages()
        this.scrollToBottom()
        return
      }
      // 普通消息
      this.messages.push({
        msgId: msg.msgId || '',
        chatSessionId: msg.chatSessionId || '',
        fromUserId: msg.fromUserId || '',
        toUserId: msg.toUserId || '',
        contentType: msg.contentType || 'plaintext',
        msg: msg.msg || '',
        parsedMsg: msg.parsedMsg || msg.msg || '',
        thinkContent: '',
        isThinking: false,
        direction: msg.direction || '1',
        createdAt: msg.createdAt || Date.now(),
        actions: []
      })
      this.scrollToBottom()
    },
    handleStreamMessage(msg) {
      if (!msg || !msg.msgId) return
      const msgId = msg.msgId
      let msgData = msg.msg
      const msgType = msg.msgType

      if (msgType === 'stream_end') {
        this.clearTimers()
        this.isSending = false
        return
      }
      if (msgType === 'stream_error') {
        this.clearTimers()
        this.isSending = false
        return
      }

      // 刷新流超时
      this.clearTimers()
      this.streamTimeoutTimer = setTimeout(() => {
        this.isSending = false
        Toast(this.$t('toast_ai_timeout'))
      }, 30000)

      // 收到第一个 stream chunk，解锁发送按钮
      if (this.isSending && !this.streamMessages[msgId]) {
        this.isSending = false
      }

      // 解析内容：stream 消息的 msg 字段是 JSON 字符串，需要解析
      let content = ''
      try {
        if (typeof msgData === 'string') {
          msgData = JSON.parse(msgData)
        }
        if (typeof msgData === 'object' && msgData !== null) {
          content = msgData.msg || ''
        }
      } catch (e) {
        console.error('[ChannelCreate] Stream parse error:', e)
        return
      }
      if (!content) return

      // 累积 stream 内容
      if (this.streamMessages[msgId]) {
        this.streamMessages[msgId] += content
      } else {
        this.streamMessages[msgId] = content
      }
      const accumulated = this.streamMessages[msgId]

      // 解析 think tags（完全模仿 ChatMsgList.vue 的逻辑）
      const parseResult = this.parseThinkTags(accumulated)

      // 更新或创建消息
      const existingIndex = this.messages.findIndex(m => m.msgId === msgId)
      if (existingIndex !== -1) {
        this.messages[existingIndex].msg = accumulated
        this.messages[existingIndex].parsedMsg = this.parseRawContent(parseResult.text)
        this.messages[existingIndex].thinkContent = parseResult.thinkContent
        this.messages[existingIndex].isThinking = parseResult.isThinking
        this.messages[existingIndex].actions = parseResult.actions
        this.messages[existingIndex].completeness = parseResult.completeness
        this.messages[existingIndex].estimatedRemainingRounds = parseResult.estimatedRemainingRounds
        // 强制触发 Vue 2 响应式更新（与 ChatMsgList.vue 一致）
        this.messages = [...this.messages]
      } else {
        this.messages.push({
          msgId: msgId,
          chatSessionId: msg.chatSessionId || this.chatSessionId,
          fromUserId: PLATFORM_USER_ID,
          toUserId: this.currentUser.userId || '',
          contentType: 'assistant',
          msg: accumulated,
          parsedMsg: this.parseRawContent(parseResult.text),
          thinkContent: parseResult.thinkContent,
          isThinking: parseResult.isThinking,
          actions: parseResult.actions,
          completeness: parseResult.completeness,
          estimatedRemainingRounds: parseResult.estimatedRemainingRounds,
          direction: '1',
          createdAt: Date.now()
        })
      }

      // 检查 requirement_clarified
      this.checkRequirementClarified(parseResult.actions, accumulated)

      // 从消息中同步进度到顶部卡片（完备度只增不减）
      this.syncProgressFromMessages()

      this.scrollToBottom()
    },

    // 从消息中同步进度状态到顶部卡片
    syncProgressFromMessages() {
      // 遍历所有消息，找到最新的 completeness 和 estimatedRemainingRounds
      let latestCompleteness = null
      let latestRounds = null
      for (const msg of this.messages) {
        if (msg.completeness != null) {
          latestCompleteness = latestCompleteness != null
            ? Math.max(latestCompleteness, msg.completeness)
            : msg.completeness
        }
        if (msg.estimatedRemainingRounds != null) {
          latestRounds = msg.estimatedRemainingRounds
        }
      }
      this.completeness = latestCompleteness
      this.estimatedRemainingRounds = latestRounds
    },

    // ===== 发送消息 =====
    async sendMessage() {
      const text = this.inputText.trim()
      if (!text || this.isSending) return

      // 立即上锁：建会话是异步请求，在途期间重复点击会产生重复消息/会话（对齐 client/ channel_create_page）
      this.isSending = true

      if (!this.chatSessionId) {
        await this.createSession(text)
      }
      if (!this.chatSessionId) {
        this.isSending = false
        Toast('Failed to create session')
        return
      }

      // 添加用户消息
      this.messages.push({
        msgId: 'local_' + Date.now(),
        fromUserId: this.currentUser.userId || '',
        toUserId: PLATFORM_USER_ID,
        contentType: 'plaintext',
        msg: text,
        parsedMsg: text,
        direction: '0',
        createdAt: Date.now()
      })
      this.inputText = ''
      this.scrollToBottom()

      // 生成 msgId
      const msgId = 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)

      // 发送消息
      wsService.sendMessage({
        chatSessionId: this.chatSessionId,
        msgId: msgId,
        toUserId: PLATFORM_USER_ID,
        contentType: 'plaintext',
        msg: text,
        // 建群向导会话不属于任何真实群：channelId 留空（历史版本发全局占位 id 20210802，
        // 会被 ai_handler 补绑到 session 且 GetChannel 必然 not found）
        elicitType: 'channel'
      })

      // ACK 超时：8 秒
      this.ackTimer = setTimeout(() => {
        if (this.isSending) {
          this.isSending = false
          this.addBotMessage('Send timed out, please try again.')
        }
      }, 8000)

      // 流超时：30 秒
      this.streamTimeoutTimer = setTimeout(() => {
        if (this.isSending) {
          this.isSending = false
          Toast(this.$t('toast_ai_timeout'))
        }
      }, 30000)
    },
    async createSession(userMsg) {
      try {
        const response = await axios.post('/api/chat/create_chat_session', {
          toUserId: PLATFORM_USER_ID,
          type: 'channel',
          userMsg: userMsg
        })
        if (response.data.code === 0 && response.data.data) {
          this.chatSessionId = String(response.data.data.chatSessionId || '')
        }
      } catch (e) {
        console.error('[ChannelCreate] createSession error:', e)
      }
    },

    // ===== 解析工具 =====
    parseThinkTags(content) {
      if (!content || typeof content !== 'string') {
        return { text: content, thinkContent: null, isThinking: false, actions: null, completeness: null, estimatedRemainingRounds: null }
      }

      let invisibleContent = ''
      let visibleText = ''
      let thinkContents = []
      let hasIncompleteThink = false

      // Step 1: 找到所有完整的 think 标签位置
      const thinkTagRegex = /<\s*think\s*>([\s\S]*?)<\s*\/\s*think\s*>/g
      const thinkMatches = []
      let match
      while ((match = thinkTagRegex.exec(content)) !== null) {
        thinkMatches.push({
          fullMatch: match[0],
          content: match[1],
          startIndex: match.index,
          endIndex: match.index + match[0].length
        })
      }

      // Step 2: 先构建临时文本（移除 think 标签），然后在 tempText 上查找 invisible
      let tempTextForInvisible = content
      for (const tm of thinkMatches) {
        tempTextForInvisible = tempTextForInvisible.replace(tm.fullMatch, '')
      }
      // 移除不完整的 think 标签
      tempTextForInvisible = tempTextForInvisible.replace(/<\s*think\s*>[\s\S]*$/, '')

      // 在移除 think 后的文本上查找 invisible 标签
      const invisibleStartMatch = tempTextForInvisible.match(/<\s*invisible\s*>/i)
      const invisibleEndMatch = tempTextForInvisible.match(/<\s*\/\s*invisible\s*>/i)
      let invisibleStart = invisibleStartMatch ? invisibleStartMatch.index : -1
      let invisibleEnd = invisibleEndMatch ? invisibleEndMatch.index + invisibleEndMatch[0].length : -1

      // Step 3: 提取 invisible 内容（从 tempTextForInvisible 中）
      if (invisibleStart !== -1 && invisibleEnd !== -1) {
        const invisibleStartTagEnd = tempTextForInvisible.indexOf('>', invisibleStart) + 1
        invisibleContent = tempTextForInvisible.substring(invisibleStartTagEnd, invisibleEndMatch.index)
      }

      // Step 4: 处理完整的 think 标签
      for (const tm of thinkMatches) {
        let thinkContent = tm.content
        // 移除 think 内所有完整的 invisible 块
        thinkContent = thinkContent.replace(/<\s*invisible\s*>[\s\S]*?<\s*\/\s*invisible\s*>/gi, '').trim()
        // 再处理不完整的 invisible（只有开始标签）
        const localInvisibleStartMatch = thinkContent.match(/<\s*invisible\s*>/i)
        if (localInvisibleStartMatch) {
          thinkContent = thinkContent.substring(0, localInvisibleStartMatch.index).trim()
        }
        thinkContents.push(thinkContent)
      }

      // Step 5: 检查不完整的 think 标签（流式传输中）
      const incompleteThinkMatch = content.match(/<\s*think\s*>([\s\S]*)$/)
      if (incompleteThinkMatch && thinkMatches.length === 0) {
        let thinkPart = incompleteThinkMatch[1]
        const closeThinkMatch = thinkPart.match(/<\s*\/\s*think\s*>/i)

        if (closeThinkMatch) {
          thinkPart = thinkPart.substring(0, closeThinkMatch.index).trim()
          thinkPart = thinkPart.replace(/<\s*invisible\s*>[\s\S]*?<\s*\/\s*invisible\s*>/gi, '').trim()
          const incompleteInvisibleMatch = thinkPart.match(/<\s*invisible\s*>/i)
          if (incompleteInvisibleMatch) {
            thinkPart = thinkPart.substring(0, incompleteInvisibleMatch.index).trim()
          }
          hasIncompleteThink = false
        } else {
          hasIncompleteThink = true
        }
        if (thinkPart) {
          thinkContents.push(thinkPart)
        }
      }

      // Step 6: 构建可见文本
      let tempText = tempTextForInvisible

      // 移除 invisible 标签及其内容
      if (invisibleStart !== -1) {
        if (invisibleEnd !== -1) {
          const invisibleRegex = /<\s*invisible\s*>[\s\S]*?<\s*\/\s*invisible\s*>/i
          tempText = tempText.replace(invisibleRegex, '')
        } else {
          const tempInvisibleMatch = tempText.match(/<\s*invisible\s*>/i)
          if (tempInvisibleMatch) {
            tempText = tempText.substring(0, tempInvisibleMatch.index)
          }
        }
      }

      visibleText = tempText.replace(/\n{3,}/g, '\n\n').trim()

      // Step 7: 解析 actions、completeness、estimated_remaining_rounds
      let actions = null
      let completeness = null
      let estimatedRemainingRounds = null
      if (!hasIncompleteThink && invisibleContent) {
        const actionsRegex = /<\s*actions\s*>\s*([\s\S]*?)\s*<\s*\/\s*actions\s*>/i
        const actionsMatch = invisibleContent.match(actionsRegex)
        if (actionsMatch) {
          try {
            const actionsStr = actionsMatch[1].replace(/\{\{/g, '{').replace(/\}\}/g, '}')
            actions = JSON.parse(actionsStr)
          } catch (e) {
            console.error('[ChannelCreate] Failed to parse actions:', e)
          }
        }

        const completenessRegex = /<\s*completeness\s*>(\d+)\s*<\s*\/\s*completeness\s*>/i
        const completenessMatch = invisibleContent.match(completenessRegex)
        if (completenessMatch) {
          const val = parseInt(completenessMatch[1], 10)
          if (!isNaN(val)) {
            completeness = Math.max(0, Math.min(100, val))
          }
        }

        const remainingRegex = /<\s*estimated_remaining_rounds\s*>(\d+)\s*<\s*\/\s*estimated_remaining_rounds\s*>/i
        const remainingMatch = invisibleContent.match(remainingRegex)
        if (remainingMatch) {
          const val = parseInt(remainingMatch[1], 10)
          if (!isNaN(val)) {
            estimatedRemainingRounds = val
          }
        }
      }

      return {
        text: visibleText,
        thinkContent: thinkContents.length > 0 ? thinkContents.join('\n') : null,
        isThinking: hasIncompleteThink,
        actions: actions,
        completeness: completeness,
        estimatedRemainingRounds: estimatedRemainingRounds
      }
    },
    checkRequirementClarified(actions, content) {
      if (!actions || actions.length === 0) return
      for (const action of actions) {
        if (action.type === 'requirement_clarified') {
          const name = this.extractTag(content, 'channel_name')
          const desc = this.extractTag(content, 'channel_description')
          this.requirementClarified = '1'
          if (name) this.extractedName = name
          if (desc) this.extractedDescription = desc
          break
        }
      }
    },
    extractTag(content, tagName) {
      // 使用 [\s\S] 代替 . 以匹配包括换行在内的所有字符，避免使用 ES2018 的 s 标志
      const regex = new RegExp('<' + tagName + '>\\s*([\\s\\S]*?)\\s*<\\/' + tagName + '>', 'i')
      const match = content.match(regex)
      return match ? match[1].trim() : null
    },
    addBotMessage(text) {
      this.messages.push({
        msgId: 'bot_' + Date.now(),
        fromUserId: PLATFORM_USER_ID,
        toUserId: this.currentUser.userId || '',
        contentType: 'assistant',
        msg: text,
        parsedMsg: this.parseRawContent(text),
        direction: '1',
        createdAt: Date.now()
      })
      this.scrollToBottom()
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatMessages
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },
    formatMsg(msg) {
      let text = msg.parsedMsg || msg.msg || ''
      if (text && typeof text === 'string') {
        text = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
      }
      return text
    },

    resetSession() {
      this.messages = []
      this.chatSessionId = ''
      this.requirementClarified = '0'
      this.extractedName = ''
      this.extractedDescription = ''
      this.completeness = null
      this.estimatedRemainingRounds = null
      this.streamMessages = {}
      this.clearTimers()
      this.isSending = false
      this.addBotMessage(this.$t('channelCreate_welcomeMsg'))
    },

    // ===== 辅助方法（模仿 ChatMsgList.vue） =====
    parseRawContent(content) {
      if (!content || typeof content !== 'string') return content
      // 转义 HTML 实体
      let text = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // 将换行转为 <br>
      text = text.replace(/\n/g, '<br>')
      return text
    },
    getLatestThinkLine(thinkContent) {
      if (!thinkContent) return ''
      const lines = thinkContent.split('\n').filter(line => line.trim())
      return lines.length > 0 ? lines[lines.length - 1] : ''
    },
    handlePickLocation(action) {
      console.log('[ChannelCreate] pick_location action:', action)
      Toast(this.$t('feature_not_available'))
    },
    handlePickDate(action) {
      console.log('[ChannelCreate] pick_date action:', action)
      Toast(this.$t('feature_not_available'))
    },
    // 当前语言码（ISO 639-1；zhHant → zh_Hant）
    _currentLang() {
      const loc = (this.$i18n && this.$i18n.locale) || 'en'
      return loc === 'zhHant' ? 'zh_Hant' : loc
    },

    // ===== 创建群 =====
    async showConfirmDialog() {
      this.creating = true
      this.similarHubs = []
      this.hasExactMatch = false
      try {
        const response = await axios.post('/api/channel/propose_create', {
          name: this.extractedName,
          description: this.extractedDescription,
          // 同语言过滤：优先 session.language（后端读，LLM 判定的用户消息语言），app locale 兜底
          chatSessionId: this.chatSessionId || '',
          lang: this._currentLang()
        })
        if (response.data.code === 0 && response.data.data) {
          const candidates = response.data.data.candidates || []
          for (const ch of candidates) {
            if (ch.name && ch.name.toLowerCase() === this.extractedName.toLowerCase()) {
              this.hasExactMatch = true
              this.similarHubs = [ch]
              break
            }
          }
          if (!this.hasExactMatch) {
            this.similarHubs = candidates
          }
        }
      } catch (e) {
        console.error('[ChannelCreate] search similar error:', e)
      }
      this.creating = false
      if (this.similarHubs.length === 0) {
        this.createNewChannel()
      } else {
        this.showSimilarHubs = true
      }
    },
    async joinSimilarHub(hub) {
      this.joiningChannelId = hub.channelId
      try {
        await axios.post('/api/channel/join', { channelId: hub.channelId })
        Toast.success(this.$t('operationSuccess'))
        this.showSimilarHubs = false
        this.joiningChannelId = ''
        this.$router.replace({ path: '/channel/' + hub.channelId })
      } catch (e) {
        console.error('[ChannelCreate] joinSimilarHub error:', e)
        this.joiningChannelId = ''
        Toast.fail(this.$t('operationFailed'))
      }
    },
    async createNewChannel() {
      this.showSimilarHubs = false
      if (!this.extractedName) return
      this.creating = true
      try {
        const response = await axios.post('/api/channel/create', {
          name: this.extractedName,
          description: this.extractedDescription,
          // 群语言：优先 session.language（后端读，LLM 判定的用户消息语言，比 UI locale 准）；
          // app locale 仅作无 session 时的兜底
          chatSessionId: this.chatSessionId || '',
          lang: this._currentLang()
        })
        if (response.data.code === 0 && response.data.data) {
          Toast.success(this.$t('channelCreate_created'))
          const channelId = String(response.data.data.channelId || '')
          if (channelId) {
            this.$router.replace({ path: '/channel/' + channelId })
          } else {
            this.$router.back()
          }
        } else {
          Toast.fail(response.data.msg || this.$t('channelCreate_failed'))
        }
      } catch (error) {
        console.error('[ChannelCreate] createNewChannel error:', error)
        Toast.fail(this.$t('channelCreate_failed'))
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style scoped>
.channel-create-page {
  background: #F7F5F2;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.channel-create-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.channel-create-title {
  font-size: 16px;
  font-weight: 600;
}

.progress-card {
  margin: 12px 12px 0 12px;
  padding: 14px;
  background: #F7F5F2;
  border-radius: 12px;
  border: 1px solid #E0DDD8;
  position: sticky;
  top: 0;
  z-index: 10;
}

.thinking-latest-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 4px;
}

.thinking-latest-text span {
  opacity: 0.7;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1A1A1A;
}

.progress-reset {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #E8543A;
  cursor: pointer;
  padding: 4px 8px;
}

.progress-steps {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.step-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.step-active {
  background: rgba(214, 78, 60, 0.1);
  color: #E8543A;
}

.step-done {
  background: rgba(76, 175, 80, 0.1);
  color: #34A853;
}

.step-pending {
  background: #E0DDD8;
  color: #8A8A8A;
}

.step-dot-active {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #E8543A;
}

.step-dot-pending {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #8A8A8A;
}

.step-line {
  flex: 1;
  height: 1px;
  margin: 0 4px;
}

.line-done {
  background: #34A853;
}

.line-pending {
  background: #E0DDD8;
}

.progress-subtitle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #8A8A8A;
  margin-bottom: 8px;
}

.progress-percent {
  font-size: 11px;
  font-weight: 700;
  color: #E8543A;
}

.progress-preview {
  background: #FFF8F6;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid rgba(214, 78, 60, 0.12);
}

.preview-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 4px;
}

.preview-label {
  font-size: 12px;
  color: #8A8A8A;
  flex-shrink: 0;
}

.preview-value {
  font-size: 13px;
  color: #1A1A1A;
  font-weight: 500;
}

.progress-notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  background: #E8F7FB;
  border-radius: 8px;
  margin-bottom: 10px;
  font-size: 11px;
  color: #2A8FAD;
  line-height: 1.4;
}

.chat-scroll-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.chat-messages {
  padding: 12px;
}

.chat-message {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.msg-sent {
  flex-direction: row-reverse;
}

.msg-avatar {
  flex-shrink: 0;
}

.msg-avatar-img {
  width: 36px;
  height: 36px;
  border-radius: 16%;
  object-fit: cover;
  background: #FEF0ED;
}

.msg-avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 16%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.msg-content-wrapper {
  max-width: 70%;
  display: flex;
  flex-direction: column;
}

.msg-sent .msg-content-wrapper {
  align-items: flex-end;
}

.msg-received .msg-content-wrapper {
  align-items: flex-start;
}

.msg-bubble {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.bubble-sent {
  background: #FEF0ED;
  color: #1A1A1A;
  border-bottom-right-radius: 2px;
}

.bubble-received {
  background: #fff;
  color: #1A1A1A;
  border-bottom-left-radius: 2px;
  text-align: left;
}

.msg-meta {
  font-size: 10px;
  color: #bbb;
  margin-top: 2px;
}

.chat-input-area {
  padding: 8px 12px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  background: white;
  border: 1px solid #E0DDD8;
  flex: 0 0 auto;
}

.chat-input-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}

.chat-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
}

.similar-hubs-popup {
  padding: 20px 16px;
}

.similar-hubs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.similar-hubs-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: bold;
  color: #1A1A1A;
}

.similar-hubs-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #E8F7FB;
  border-radius: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #2A8FAD;
  font-weight: 600;
}

.similar-hubs-hint.exact-match {
  background: #E8F5E9;
  color: #34A853;
  border: 1px solid #E8F5E9;
}

.similar-hubs-list {
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.similar-hub-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: white;
  border-radius: 10px;
  border: 1px solid #E0DDD8;
  margin-bottom: 8px;
}

.similar-hub-avatar {
  flex-shrink: 0;
}

.similar-hub-avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 16%;
  object-fit: cover;
}

.similar-hub-avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 16%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: bold;
}

.similar-hub-info {
  flex: 1;
  min-width: 0;
}

.similar-hub-name {
  font-size: 14px;
  font-weight: 600;
  color: #1A1A1A;
}

.similar-hub-desc {
  font-size: 12px;
  color: #8A8A8A;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.similar-hub-meta {
  font-size: 12px;
  color: #8A8A8A;
  margin-top: 2px;
}

.similar-hubs-empty {
  padding: 20px;
  color: #8A8A8A;
  font-size: 14px;
  text-align: center;
}

.similar-hubs-footer {
  margin-top: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/ChannelCreate.vue